import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";

import LoginIcon from "@mui/icons-material/Login";
import {
  Box,
  CardContent,
  Typography,
  Grid2,
  Paper,
  IconButton,
} from "@mui/material";

import { ActionButton } from "components/ActionButton";
import { Tooltip } from "components/Tooltip";
import { FrmTextField } from "components/formFields/FrmTextField";
import { ButtonProgress } from "components/utility/ButtonProgress";
import { FormErrors } from "components/utility/FormErrors";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import { resetPassword } from "services/sosInventoryService/sosApi";

import { useErrors } from "hooks/useErrors";

import { initialErrorState } from "views/common";

import { openAlert } from "globalState/alertSlice";

const sx = {
  paper: { p: 4, width: "40rem", display: "flex", justifyContent: "center" },
  background: {
    minHeight: "100vh",
    width: "100%",
    backgroundImage: "linear-gradient(#b0c8f6, #5b8dee)",
    justifyContent: "center",
    alignItems: "center",
  },
};

export function ResetPassword() {
  const [passwords, setPasswords] = useState({
    newPassword: null,
    confirmNewPassword: null,
  });
  const { newPassword, confirmPassword } = passwords;
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { errors, setErrors, isInError } = useErrors();

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const token = queryParams.get("token");

    if (!token) {
      navigate("/login");
    }
  }, [search, navigate]);

  async function handleSubmit(e) {
    e?.preventDefault();

    if (newPassword !== confirmPassword) {
      const errors = initialErrorState();
      errors.messages.push(i18n("isValid.PasswordMismatch"));
      errors.entity.push("newPassword");
      errors.entity.push("confirmNewPassword");
      setErrors(errors);
      return;
    }
    // clear prior errors if there were any
    setErrors(initialErrorState());

    const queryParams = new URLSearchParams(search);
    const token = queryParams.get("token");
    setIsLoading(true);
    try {
      const { success, message } = await resetPassword({ newPassword, token });
      if (success) {
        const message = i18n("password.ResetPasswordSubmitMessage");
        dispatch(openAlert({ type: "success", message }));
        setPasswords({ newPassword: null, confirmNewPassword: null });
        navigate("/login");
      } else {
        const errorMessage =
          message || i18n("password.ResetPasswordDefaultError");
        dispatch(openAlert({ type: "error", message: errorMessage }));
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  }

  return (
    <Grid2 container spacing={0} sx={sx.background}>
      <Paper sx={sx.paper} elevation={5}>
        <Box sx={{ display: "grid" }} m={2} mb={0}>
          <Box mb={1}>
            <Typography variant="h3">{i18n("password.Reset.title")}</Typography>
          </Box>
          <Box>
            <CardContent>
              <FormErrors width="100%" errors={errors} setErrors={setErrors} />
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  mb={2}
                >
                  <FrmTextField
                    required
                    type="password"
                    label={i18n("frmLabel.NewPassword")}
                    value={newPassword}
                    onValueChange={(_, newPassword) =>
                      setPasswords((prev) => ({ ...prev, newPassword }))
                    }
                    disabled={isLoading}
                    error={isInError("newPassword")}
                  />
                  <FrmTextField
                    required
                    type="password"
                    label={i18n("frmLabel.ConfirmNewPassword")}
                    value={confirmPassword}
                    onValueChange={(_, confirmPassword) =>
                      setPasswords((prev) => ({ ...prev, confirmPassword }))
                    }
                    disabled={isLoading}
                    error={isInError("confirmNewPassword")}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title={i18n("password.GoBackToLogin")}>
                      <IconButton component="a" href="/login" size="large">
                        <LoginIcon />
                      </IconButton>
                    </Tooltip>
                    <div style={{ position: "relative" }}>
                      <ActionButton type="submit" disabled={isLoading}>
                        {i18n("password.ChangePassword")}
                      </ActionButton>
                      {isLoading && <ButtonProgress />}
                    </div>
                  </Box>
                </Box>
              </form>
            </CardContent>
          </Box>
          <VSpace space={1} />
        </Box>
      </Paper>
    </Grid2>
  );
}
